<template>
  <div
    v-if="budgetItem.materials.length > 0"
    class="mb-8"
  >
    <span class="block font-bold text-lg">Materials</span>
    <form.Field name="materials">
      <template #default="{ field }">
        <div
          v-for="(material,index) in budgetItem.materials"
          :key="(material as any)"
          class="grid grid-cols-6 gap-4 mt-6"
        >
          <div class="col-span-2 flex flex-col justify-center">
            <form.Field
              :key="`material-${index}`"
              :name="(`materials[${index}].id` as any)"
              :validators="{
                onChange: yup.string().required('Material is required')
              }"
            >
              <template #default="{ field: subField }">
                <MaterialsAutoComplete
                  v-model="material.id"
                  :name="subField.name"
                  label="Items"
                  placeholder="Materials"
                  :disabled="isSaving"
                  :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                  no-error-messages
                  @change="onChangeMaterial($event, index)"
                />
              </template>
            </form.Field>
          </div>

          <!-- Quantity -->
          <div class="flex flex-col justify-center">
            <form.Field
              :key="`quantity-${index}`"
              :name="(`materials[${index}].quantity` as any)"
              :validators="{
                onChange: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Quantity must be a positive number',isNumber).required('Quantity is required')
              }"
            >
              <template #default="{ field: subField }">
                <BaseInput
                  v-model.number="material.quantity"
                  :name="subField.name"
                  label="Quantity"
                  type="number"
                  class="-mb-4"
                  placeholder="Quantity"
                  :disabled="isSaving"
                  :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                  no-error-messages
                  @blur="updateCost()"
                  @input="(e) => subField.handleChange(e.target.value)"
                />
              </template>
            </form.Field>
          </div>

          <!-- Unit Cost -->
          <div class="flex flex-col">
              <span class="block select-none text-base/6 text-slate-950 sm:text-sm/6 font-medium">Unit Cost</span>
              <p class="my-auto"> {{ currency(material.cost) }} </p>
          </div>

          <!-- Subtotal -->
          <div class="flex flex-col">
            <span class="block select-none text-base/6 text-slate-950 sm:text-sm/6 font-medium">Subtotal</span>
            <p class="my-auto"> {{ currency(material.cost * material.quantity) }} </p>
          </div>

          <!-- Actions -->
          <div class="flex flex-col justify-center">
            <span
              v-show="!isSaving"
              :disabled="disableDeleteMaterial"
              :class="[
                'i-heroicons-trash',
                disableDeleteMaterial ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
              ]"
              @click="disableDeleteMaterial ? null : deleteMaterial(index, field)"
            />
          </div>
        </div>
      </template>
    </form.Field>

    <div class="mt-8 flex justify-center">
      <BaseButton
        key="add-material"
        variant="base"
        text="Add material"
        class="w-full"
        :class="isSaving || !enableAddMaterial ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'"
        :disabled="isSaving || !enableAddMaterial"
        @click="addMaterial"
      />
    </div>
  </div>

  <div
    v-if="budgetItem.labor.length > 0"
    class="mb-8"
  >
    <span class="block font-bold text-lg">Labor</span>
    <form.Field name="labor">
      <template #default="{ field }">
        <div
          v-for="(labor,index) in budgetItem.labor"
          :key="(labor as any)"
          class="grid grid-cols-6 gap-4 mt-6"
        >
          <div class="col-span-2 flex flex-col justify-center">
            <form.Field
              :key="`labor-${index}`"
              :name="(`labor[${index}].id` as any)"
              :validators="{
                onChange: yup.string().required('Labor is required')
              }"
            >
              <template #default="{ field: subField }">
                <LaborsAutoComplete
                  v-model="labor.id"
                  :name="subField.name"
                  label="Role/Resource"
                  placeholder="Labors"
                  :disabled="isSaving"
                  :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                  no-error-messages
                  @change="onChangeLabor($event, index)"
                />
              </template>
            </form.Field>
          </div>

          <!-- Hours -->
          <div class="flex flex-col justify-center">
            <form.Field
              :key="`hours-${index}`"
              :name="(`labor[${index}].hours` as any)"
              :validators="{
                onChange: yup.string().transform((value) => value.replace(/,/g, '')).test('is-number','Hours must be a positive number',isNumber).required('Hours is required')
              }"
            >
              <template #default="{ field: subField }">
                <BaseInput
                  v-model.number="labor.hours"
                  :name="subField.name"
                  label="Hours"
                  placeholder="Hours"
                  type="number"
                  class="-mb-4"
                  :disabled="isSaving"
                  :error="(subField.state.meta.errors && subField.state.meta.errors[0] as string)"
                  no-error-messages
                  @blur="updateCost()"
                  @input="(e) => subField.handleChange(e.target.value)"
                />
              </template>
            </form.Field>
          </div>

          <!-- Hourly Rate -->
          <div class="flex flex-col">
              <span class="block select-none text-base/6 text-slate-950 sm:text-sm/6 font-medium">Hourly Rate</span>
              <p class="my-auto"> {{ labor.hourlyRate }} </p>
          </div>

          <!-- Subtotal -->
          <div class="flex flex-col">
            <span class="block select-none text-base/6 text-slate-950 sm:text-sm/6 font-medium">Subtotal</span>
            <p class="my-auto"> {{ currency(labor.hours * labor.hourlyRate) }} </p>
          </div>

          <!-- Actions -->
          <div class="flex flex-col justify-center">
            <span
              v-show="!isSaving"
              :disabled="disableDeleteLabor"
              :class="[
                'i-heroicons-trash',
                disableDeleteLabor ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
              ]"
              @click="disableDeleteLabor ? null : deleteLabor(index, field)"
            />
          </div>
        </div>
      </template>
    </form.Field>

    <div class="mt-8 flex justify-center">
      <BaseButton
        key="add-labor"
        text="Add labor"
        class="w-full"
        :class="isSaving || !enableAddLabor ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'"
        :disabled="isSaving || !enableAddLabor"
        @click="addLabor"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Material } from '@/models/material'
import { Labor } from '@/models/labor'
import MaterialsAutoComplete from '@/components/base/MaterialsAutoComplete.vue'
import LaborsAutoComplete from '@/components/base/LaborsAutoComplete.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import { extend, omit } from 'underscore'
import BaseButton from '@/components/base/BaseButton.vue'
import { currency, toFixed, toNumber } from '@/utils'
import { computed, onMounted, PropType, ref, watch } from 'vue'
import { BudgetItem } from '@/models/budget'
import { useForm } from '@tanstack/vue-form'
import * as yup from 'yup'
import { yupValidator } from '@tanstack/yup-form-adapter'

const budgetItem = defineModel({
  type: Object as PropType<BudgetItem>,
  default: () => new BudgetItem()
})

const props = defineProps({
  isSaving: {
    type: Boolean,
    default: false
  },
  enableLabor: {
    type: Boolean,
    default: false
  },
  enableMaterials: {
    type: Boolean,
    default: false
  },
  isOpen: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['calculate-total', 'can-submit'])

const form = useForm({
  defaultValues: {
    materials: budgetItem.value.materials,
    labor: budgetItem.value.labor
  },
  validatorAdapter: yupValidator()
})
const canSubmit = ref(false)
const emitCanSubmit = () => {
  const isValid = form.useStore(state =>  state.canSubmit)
  if(isValid.value !== canSubmit.value) {
    canSubmit.value = isValid.value
    emit('can-submit', isValid.value)
  }
}

watch(() => props.isOpen, (isOpen) => {
  if (isOpen) {
    form.validateAllFields('change')
    emitCanSubmit()
  }
})

watch(() => props.enableLabor, async () => {
  if(props.enableLabor) {
    if (budgetItem.value.labor.length === 1 && !budgetItem.value.labor[0].id) {
      form.setFieldValue(`labor[${0}].id`, null, {dontUpdateMeta: true})
      await form.replaceFieldValue('labor', 0, budgetItem.value.labor[0], {dontUpdateMeta: true})
    }
  } else {
    const labors = form.getFieldValue('labor')
    for (let index = 0; index < labors.length; index++) {
      await form.removeFieldValue('labor', index)
    }
    form.setFieldValue('labor', [], {dontUpdateMeta: true})
  }
  emitCanSubmit()
}, { immediate: true })

watch(() => props.enableMaterials, async () => {
  if(props.enableMaterials) {
    if (budgetItem.value.materials.length === 1 && !budgetItem.value.materials[0].id) {
      form.setFieldValue(`materials[${0}].id`, null, {dontUpdateMeta: true})
      await form.replaceFieldValue('materials', 0, budgetItem.value.materials[0], {dontUpdateMeta: true})
    }
  } else {
    const materials = form.getFieldValue('materials')
    for (let index = 0; index < materials.length; index++) {
      await form.removeFieldValue('materials', index)
    }
    form.setFieldValue('materials', [], {dontUpdateMeta: true})
  }
  emitCanSubmit()
}, { immediate: true })

const updateCost = () => {
  calculateCostItemTotal()
  emit('calculate-total', budgetItem.value)
  emitCanSubmit()
}

const addMaterial = async () => {
  const material = new Material()
  material.quantity = 1
  material.id = ''
  budgetItem.value.materials.push(Object.assign({}, material))
  form.setFieldValue(`materials[${budgetItem.value.materials.length - 1}].id`, null, {dontUpdateMeta: true})
  await form.replaceFieldValue('materials', budgetItem.value.materials.length - 1, material)
  emitCanSubmit()
}

const addLabor = async () => {
  const labor = new Labor()
  labor.hours = 1
  labor.id = ''
  budgetItem.value.labor.push(Object.assign({}, labor))
  form.setFieldValue(`labor[${budgetItem.value.labor.length - 1}].id`, null, {dontUpdateMeta: true})
  await form.replaceFieldValue('labor',  budgetItem.value.labor.length - 1, labor)
  emitCanSubmit()
}

const onChangeMaterial = async (selectedMaterial: Material, index: number) => {
  extend(budgetItem.value.materials[index], omit(selectedMaterial, ['label', 'value']))
  // field.replaceValue(index, {
  //   ...budgetItem.value.materials[index]
  // })
  await form.replaceFieldValue('materials', index, budgetItem.value.materials[index])
  updateCost()
}

const onChangeLabor = async (selectedLabor: Labor, index: number) => {
  extend(budgetItem.value.labor[index], Object.assign({}, omit(selectedLabor, ['label', 'value'])))
  // field.replaceValue(index, {
  //   ...budgetItem.value.labor[index]
  // })
  await form.replaceFieldValue('labor', index, budgetItem.value.labor[index])
  updateCost()
}

const deleteMaterial = async (index, field) => {
  budgetItem.value.materials.splice(index, 1)
  await field.removeValue(index)
  updateCost()
}

const deleteLabor = async (index, field) => {
  budgetItem.value.labor.splice(index, 1)
  await field.removeValue(index)
  updateCost()
}

const disableDeleteMaterial = computed(() => {
  return budgetItem.value.materials.length === 1
})

const disableDeleteLabor = computed(() => {
  return budgetItem.value.labor.length === 1
})

const calculateCostItemTotal = () => {
  // set total to service cost
  let total = 0
  let materialsBudget = 0
  let laborBudget = 0

  // if service has material or labor caculate the sum for the total
  if (budgetItem.value.materials.length > 0 || budgetItem.value.labor.length > 0) {
    // materials cost
    budgetItem.value.materials.map((material) => {
      materialsBudget += toFixed(material.quantity * toNumber(material.cost))
    })
    // labor cost
    budgetItem.value.labor.map((labor) => {
      laborBudget += toFixed(labor.hours * toNumber(labor.hourlyRate))
    })
  }

  total = toFixed(materialsBudget) + toFixed(laborBudget)
  budgetItem.value.materialsBudget = toFixed(materialsBudget)
  budgetItem.value.laborBudget = toFixed(laborBudget)
  budgetItem.value.cost = toFixed(total)
}

const isNumber = (value) => {
  return !isNaN(Number(value)) && Number(value) > 0
}

const enableAddLabor = computed(() => {
  let enable = true
  if (budgetItem.value.labor.length > 0) {
    budgetItem.value.labor.map((labor) => {
      if (!labor.id || !labor.hours) {
        enable = false
      }
    })
  }
  return enable
})

const enableAddMaterial = computed(() => {
  let enable = true
  if (budgetItem.value.materials.length > 0) {
    budgetItem.value.materials.map((material) => {
      if (!material.id || !material.quantity) {
        enable = false
      }
    })
  }
  return enable
})


onMounted(() => {
  form.validateAllFields('change')
  emitCanSubmit()
})
</script>
